import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { BACKEND_URL as API } from '@/utils/apiConfig';
import { MessageDisplay } from '@/components/MessageDisplay';
import IOSAppBanner from '@/components/IOSAppBanner';

import { useTranslation } from 'react-i18next';

import { homeTarget } from '@/components/ShareHomeButton';
const FOLLOW_UP_PATTERNS = [
  /^would you like/i, /^do you want/i, /^shall (i|we)/i, /^should (i|we)/i,
  /^is there (anything|something|a specific)/i, /^are there (any|other)/i,
  /^did (that|this) (make sense|help|answer)/i, /^does (that|this) (make sense|help|answer)/i,
  /^what (would|do|else|other|aspect)/i, /^how (would|do|can|else)/i,
  /^let me know/i, /^feel free/i, /^i'd love to (explore|help|dive|discuss)/i,
  /^i can (also|help|explore|provide)/i,
];

function stripFollowUpQuestions(text) {
  const paragraphs = text.split('\n\n');
  const filtered = paragraphs.filter((p) => {
    const trimmed = p.trim();
    if (trimmed.length >= 200) return true;
    if (trimmed.endsWith('?') || FOLLOW_UP_PATTERNS.some((re) => re.test(trimmed))) return false;
    return true;
  });
  return filtered.join('\n\n').trimEnd();
}

export default function DeepLinkPage() {
  const {
    t
  } = useTranslation();

  const { linkId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [publicConv, setPublicConv] = useState(null);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [convTitle, setConvTitle] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const resolve = async () => {
      try {
        const { data } = await axios.get(`${API}/api/deeplinks/resolve/${linkId}`);
        if (data.type === 'conversation') {
          if (data.requires_password) {
            setRequiresPassword(true);
            setConvTitle(data.title || 'Shared Conversation');
            setLoading(false);
          } else if (data.is_public && data.messages?.length > 0) {
            setPublicConv(data);
            setLoading(false);
          } else if (!data.is_public && !data.requires_password) {
            // Private without password — requires login
            navigate(`/chat?conversation=${data.conversation_id}`, { replace: true });
          } else {
            navigate('/app', { replace: true });
          }
        } else {
          navigate('/app', { replace: true });
        }
      } catch (err) {
        if (err.response?.status === 410) {
          setError('This shared link has been disabled by the owner.');
        } else {
          setError('This link is no longer available.');
        }
        setLoading(false);
      }
    };
    resolve();
  }, [linkId, navigate]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) return;
    setVerifying(true);
    setPasswordError('');
    try {
      const { data } = await axios.post(`${API}/api/deeplinks/verify/${linkId}`, { password: password.trim() });
      setPublicConv(data);
      setRequiresPassword(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setPasswordError('Incorrect password. Please try again.');
      } else if (err.response?.status === 410) {
        setError('This shared link has been disabled by the owner.');
        setRequiresPassword(false);
      } else {
        setPasswordError('Something went wrong. Please try again.');
      }
    }
    setVerifying(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F172A]" data-testid="deeplink-loading">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-slate-400 text-sm">{t('generated.deeplinkpage_opening_82da')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F172A]" data-testid="deeplink-error">
        <div className="text-center space-y-4 p-8 max-w-sm">
          <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
            <AlertTriangle className="h-6 w-6 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-slate-100">{t('generated.deeplinkpage_link_unavailable_347d')}</h2>
          <p className="text-slate-400 text-sm">{error}</p>
          <button onClick={() => navigate(homeTarget(), { replace: true })} className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-500 transition" data-testid="deeplink-home-btn">
            {t('generated.deeplinkpage_go_to_eili_47f9')}
          </button>
        </div>
      </div>
    );
  }

  // Password gate
  if (requiresPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F172A] p-4" data-testid="deeplink-password-gate">
        <IOSAppBanner title={convTitle || 'Protected Conversation'} subtitle="Password-protected conversation from Eili" />
        <div className="w-full max-w-sm text-center space-y-6">
          <div className="flex items-center gap-3 justify-center mb-2">
            <img src="/logo.png" alt={t('generated.deeplinkpage_eili_86ed')} className="h-8 w-8 cursor-pointer" onClick={() => navigate(homeTarget(), { replace: true })} />
            <span className="font-semibold text-lg text-slate-100" style={{ fontFamily: "'Playwrite ES Deco', cursive" }}>{t('generated.deeplinkpage_eili_86ed')}</span>
          </div>

          <div className="w-14 h-14 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto">
            <Lock className="h-7 w-7 text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-100">{t('generated.deeplinkpage_protected_conversation_bc9d')}</h2>
            <p className="text-sm text-slate-400 mt-1">{convTitle}</p>
            <p className="text-xs text-slate-500 mt-2">{t('generated.deeplinkpage_enter_the_password_f4c4')}</p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-3">
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordError(''); }}
                placeholder={t('generated.deeplinkpage_enter_password_7c39')}
                className="w-full px-4 py-3 pr-10 rounded-xl bg-slate-800/80 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 placeholder:text-slate-500"
                autoFocus
                data-testid="deeplink-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {passwordError && (
              <p className="text-xs text-red-400" data-testid="deeplink-password-error">{passwordError}</p>
            )}
            <button
              type="submit"
              disabled={!password.trim() || verifying}
              className="w-full py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-500 transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="deeplink-password-submit"
            >
              {verifying ? 'Verifying...' : 'Unlock Conversation'}
            </button>
          </form>

          <button onClick={() => navigate(homeTarget(), { replace: true })} className="text-xs text-slate-500 hover:text-slate-300 transition">
            {t('generated.deeplinkpage_or_go_to_f061')}
          </button>
        </div>
      </div>
    );
  }

  // Public conversation view
  if (publicConv) {
    const firstMsg = publicConv.messages?.find(m => m.role === 'assistant');
    const snippet = firstMsg ? firstMsg.content.substring(0, 120) + '...' : 'Shared conversation from Eili';
    return (
      <div className="min-h-screen bg-[#0F172A] text-slate-100" data-testid="deeplink-public-view">
        <IOSAppBanner title={publicConv.title || 'Shared Conversation'} subtitle={snippet} />
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="flex items-center gap-3 mb-6">
            <img src="/logo.png" alt={t('generated.deeplinkpage_eili_86ed')} className="h-8 w-8 cursor-pointer" onClick={() => navigate(homeTarget(), { replace: true })} />
            <h1 className="text-lg font-bold" style={{ fontFamily: "'Playwrite ES Deco', cursive" }}>{t('generated.deeplinkpage_eili_86ed')}</h1>
          </div>
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-slate-100" data-testid="public-conv-title">{publicConv.title || 'Shared Conversation'}</h2>
            <p className="text-sm text-slate-400 mt-1">{t('generated.deeplinkpage_shared_conversation_from_b4da')}</p>
          </div>
          <div className="space-y-4">
            {publicConv.messages.map((msg, i) => (
              <div
                key={i}
                className={`rounded-xl px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-blue-600/20 border border-blue-600/30 ml-8'
                    : 'bg-slate-800/60 border border-slate-700/50 mr-4'
                }`}
                data-testid={`public-message-${i}`}
              >
                <p className="text-xs font-medium mb-1.5 text-slate-400">
                  {msg.role === 'user' ? 'Question' : 'Eili'}
                </p>
                <div className="text-sm leading-relaxed whitespace-pre-wrap text-slate-200">
                  {msg.role === 'assistant' ? (
                    <MessageDisplay content={stripFollowUpQuestions(msg.content)} />
                  ) : (
                    msg.content
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <button onClick={() => navigate(homeTarget(), { replace: true })} className="px-6 py-2.5 bg-blue-600 text-white rounded-full hover:bg-blue-500 transition font-medium" data-testid="deeplink-try-eili-btn">
              {t('generated.deeplinkpage_try_eili_biblical_a81f')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
